import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import tokensCss from "@/theme/tokens.css?inline";
import { FONTS_STYLE_ID, fontsCss, ensureAssetFont } from "@/theme/fonts";
import { QuizWidget } from "@/QuizWidget";
import { createTransport } from "@/transport";
import { fetchBranding } from "@/theme/fetchBranding";
import { brandingToCss, type Branding } from "@/theme/branding";
import { LogoHeader } from "@/components/LogoHeader";
import { WIDGET_ORIGIN } from "@/embed/widgetOrigin";
import { createBrandingController, type BrandingController } from "@/embed/brandingController";

export const ELEMENT_TAG = "quiz-widget";
export const BRAND_STYLE_ID = "quizkit-brand";
export const SCROLL_UNLOCK_STYLE_ID = "quizkit-scroll-unlock";

/** Normalize a host-supplied `data-api-base` to a bare, trusted origin
 * (`scheme://host[:port]`, no path/query/fragment) before it is used to build
 * `fetch()` URLs and interpolated into the `@font-face` `url()`. `URL.origin`
 * strips any `)`/`}`/`;`/path payload a malicious value could smuggle into the
 * CSS sink. Returns "" for empty, relative, or non-http(s) values so the caller
 * falls back to WIDGET_ORIGIN. */
export function normalizeApiBase(raw: string | null | undefined): string {
  if (!raw) return "";
  try {
    const u = new URL(raw);
    if (u.protocol !== "https:" && u.protocol !== "http:") return "";
    return u.origin;
  } catch {
    return "";
  }
}

/** Inject @font-face at document level (shadow-root @font-face is unreliable). */
export function ensureFontsInjected(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(FONTS_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = FONTS_STYLE_ID;
  style.textContent = fontsCss;
  document.head.appendChild(style);
}

/** Inject @font-face for any ASSET fonts this brand uses (core fonts are already
 * inlined). Safe to call repeatedly; ensureAssetFont is idempotent per key. */
export function injectBrandFonts(branding: Branding | null, origin: string): void {
  if (!branding?.fonts || !origin) return;
  for (const key of Object.values(branding.fonts)) {
    if (key) ensureAssetFont(key, origin);
  }
}

/** Keep the host page scrollable while the immersive quiz owns it.
 *
 * Third-party app embeds on host storefronts (seen live on usemanly.com.au:
 * a popup app's launcher runs even when its UI is hidden) set an inline
 * `body { overflow: hidden }` scroll lock at mobile widths and never release
 * it. In `data-layout="full"` the quiz IS the page, and any screen taller
 * than the viewport (the result funnel) becomes unreachable by wheel/touch.
 * A document-level stylesheet rule with `!important` outranks a non-important
 * inline style, so this restores user scrolling no matter when the lock is
 * applied. Full layout only: card embeds live inside someone else's page and
 * must not override its scroll behavior. Never removed on disconnect (a
 * full-layout page is a dedicated quiz page for its whole lifetime). */
export function ensureScrollUnlocked(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(SCROLL_UNLOCK_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = SCROLL_UNLOCK_STYLE_ID;
  style.textContent = "body { overflow: visible !important; }";
  document.head.appendChild(style);
}

class QuizWidgetElement extends HTMLElement {
  private _root: Root | null = null;
  private readonly _shadow: ShadowRoot;
  private _transport: ReturnType<typeof createTransport> | null = null;
  private _apiBase = "";
  private _preview = false;
  private _brandStyle: HTMLStyleElement | null = null;
  private _mount: HTMLDivElement | null = null;
  private _scaffolded = false; // guard: build shadow children exactly once
  // Branding lifecycle (dedup, abort, reconnect-reset, CSS application) is owned
  // by this plain controller; the element keeps only the DOM lifecycle.
  private readonly _branding: BrandingController;

  constructor() {
    super();
    this._shadow = this.attachShadow({ mode: "open" });
    this._branding = createBrandingController({
      fetchBranding,
      applyCss: (branding) => this._applyBranding(branding),
    });
  }

  /** Controller callback: paint the brand style node, inject fonts, re-render. */
  private _applyBranding(branding: Branding | null): void {
    injectBrandFonts(branding, this._apiBase || WIDGET_ORIGIN);
    if (this._brandStyle) {
      this._brandStyle.textContent = branding ? brandingToCss(branding) : "";
    }
    // The clear path (branding === null) only happens synchronously inside
    // start(), which is itself called from _render() — whose own _root.render()
    // is about to run. Re-rendering here too would be a redundant re-entrant
    // render. Re-render only on the async resolve path (logo must now appear),
    // matching the original _maybeFetchBranding which cleared without rendering.
    if (branding) this._render();
  }

  static get observedAttributes(): string[] {
    return ["data-quiz", "quiz", "data-api-base", "data-layout", "data-preview"];
  }

  connectedCallback(): void {
    ensureFontsInjected();

    // Build shadow DOM scaffold exactly once across any number of
    // disconnect→reconnect cycles. The nodes persist in the shadow root even
    // when the element is detached, so we simply reuse them.
    if (!this._scaffolded) {
      const style = document.createElement("style");
      style.textContent = tokensCss;
      this._shadow.appendChild(style);

      // Brand override: AFTER tokens.css so its :host vars win on source order.
      this._brandStyle = document.createElement("style");
      this._brandStyle.id = BRAND_STYLE_ID;
      this._shadow.appendChild(this._brandStyle);

      const mount = document.createElement("div");
      mount.setAttribute("data-quiz-mount", "");
      this._shadow.appendChild(mount);
      this._mount = mount;

      this._scaffolded = true;
    }

    // React roots cannot be reused after .unmount(). Create a fresh root on
    // the same persistent mount div every time we connect.
    this._root = createRoot(this._mount!);
    this._render();
  }

  attributeChangedCallback(): void {
    if (this._root) this._render();
  }

  disconnectedCallback(): void {
    this._root?.unmount();
    this._root = null;
    // Aborts any in-flight fetch and clears the cached key only when branding
    // never resolved — forcing a fresh fetch on reconnect. When branding already
    // resolved, the brand <style> node retains its textContent across
    // detach/reattach, so the controller keeps the key (no refetch, no flash).
    this._branding.reset();
  }

  private _render(): void {
    if (this.getAttribute("data-layout") === "full") ensureScrollUnlocked();
    const slug = this.getAttribute("data-quiz") ?? this.getAttribute("quiz") ?? "";
    const apiBase = normalizeApiBase(this.getAttribute("data-api-base")) || WIDGET_ORIGIN;
    const preview = this.getAttribute("data-preview") === "1";
    if (apiBase !== this._apiBase || preview !== this._preview) {
      this._apiBase = apiBase;
      this._preview = preview;
      this._transport = apiBase ? createTransport(apiBase, { preview }) : null;
    }
    void this._branding.start(slug, apiBase);
    this._root?.render(
      <StrictMode>
        <LogoHeader logo={this._branding.branding?.logo} />
        <QuizWidget slug={slug} onSubmit={this._transport?.onSubmit} onEvent={this._transport?.onEvent} />
      </StrictMode>,
    );
  }
}

export function registerElement(): void {
  if (typeof customElements !== "undefined" && !customElements.get(ELEMENT_TAG)) {
    customElements.define(ELEMENT_TAG, QuizWidgetElement);
  }
}

/** Upgrade any plain `<div data-quiz="...">` placeholder into the custom element,
 * carrying over the author's attributes (`id`, `data-layout`, `data-api-base`,
 * `class`, …) so the immersive `data-layout="full"` embed renders as written and
 * external CSS keeps targeting the author's id.
 *
 * `data-preview` is the ONE attribute deliberately NOT carried over: a public,
 * copy-paste card embed must always run live analytics. Preview mode is reserved
 * for the admin hosted mount, which constructs the element directly (see
 * hosted/mount.ts) rather than going through this div-upgrade path. */
export function upgradeMounts(): void {
  if (typeof document === "undefined") return;
  const nodes = document.querySelectorAll(`div[data-quiz]:not(${ELEMENT_TAG} div)`);
  nodes.forEach((node) => {
    const slug = node.getAttribute("data-quiz");
    if (!slug) return;
    const el = document.createElement(ELEMENT_TAG);
    for (const attr of Array.from(node.attributes)) {
      if (attr.name === "data-preview") continue;
      el.setAttribute(attr.name, attr.value);
    }
    node.replaceWith(el);
  });
}

function init(): void {
  ensureFontsInjected();
  registerElement();
  upgradeMounts();
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
}
